import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  Grid3X3, 
  FileText, 
  LogOut, 
  Menu, 
  Users,
  Store,
  ChevronRight,
  Settings,
  Bell,
  Search,
  MessageSquareQuote,
  HelpCircle,
  Home
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useAuth } from '@/context/AuthContext';

// Nova logo Carvalho Achados
const LOGO_URL = "https://customer-assets.emergentagent.com/job_instant-site-pass/artifacts/oznhzql0_image.png";

const navItems = [
  { href: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { href: '/admin/home', label: 'Gerenciar Home', icon: Home },
  { href: '/admin/produtos', label: 'Produtos', icon: Package },
  { href: '/admin/categorias', label: 'Categorias', icon: Grid3X3 },
  { href: '/admin/plataformas', label: 'Plataformas', icon: Store },
  { href: '/admin/leads', label: 'Leads', icon: Users },
  { href: '/admin/depoimentos', label: 'Depoimentos', icon: MessageSquareQuote },
  { href: '/admin/faq', label: 'FAQ', icon: HelpCircle },
  { href: '/admin/blog', label: 'Blog', icon: FileText },
  { href: '/admin/configuracoes', label: 'Configuracoes', icon: Settings },
];

export const AdminLayout = ({ children, title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { admin, logout, isAuthenticated, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated()) {
      navigate('/admin/login');
    }
  }, [loading, isAuthenticated, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated()) return null;

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.href;
    return location.pathname.startsWith(item.href);
  };

  const NavContent = () => (
    <div className="flex flex-col h-full bg-[#0f172a]">
      {/* Logo */}
      <div className="p-5 border-b border-slate-700/50">
        <Link to="/" className="flex items-center gap-3">
          <img 
            src={LOGO_URL} 
            alt="Carvalho Comercio" 
            className="h-10 w-auto object-contain bg-white rounded-lg p-1"
          />
          <span className="text-white font-bold text-sm hidden lg:block">Admin</span>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 overflow-y-auto">
        <p className="px-3 mb-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Menu</p>
        <ul className="space-y-0.5">
          {navItems.map((item) => (
            <li key={item.href}>
              <Link
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                  isActive(item)
                    ? 'bg-orange-500/15 text-orange-400 font-medium'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <item.icon className={`w-4.5 h-4.5 ${isActive(item) ? 'text-orange-400' : ''}`} />
                <span className="text-sm">{item.label}</span>
                {isActive(item) && (
                  <div className="ml-auto w-1.5 h-1.5 bg-orange-400 rounded-full" />
                )}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-slate-700/50">
        <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-xl">
          <div className="w-9 h-9 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
            {admin?.name?.charAt(0).toUpperCase() || 'A'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{admin?.name || 'Administrador'}</p>
            <p className="text-xs text-slate-500 truncate">{admin?.email}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="text-slate-500 hover:text-red-400 hover:bg-red-500/10"
            title="Sair"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-0 h-full w-64 bg-[#0f172a] border-r border-slate-700/30 z-40">
        <NavContent />
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-[#0f172a] border-b border-slate-700/30 z-40">
        <div className="flex items-center justify-between h-full px-4">
          <div className="flex items-center gap-3">
            <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white hover:bg-slate-800">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0 border-none bg-[#0f172a]">
                <NavContent />
              </SheetContent>
            </Sheet>
            <img 
              src={LOGO_URL} 
              alt="Carvalho Comercio" 
              className="h-8 w-auto object-contain bg-white rounded p-0.5"
            />
          </div>
          <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
            <Bell className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-14 lg:pt-0 min-h-screen">
        {/* Top Bar */}
        <div className="hidden lg:flex items-center justify-between bg-white border-b border-slate-200 px-6 py-3">
          <div>
            <div className="flex items-center gap-2 text-xs text-slate-400 mb-0.5">
              <Link to="/admin" className="hover:text-slate-600">Admin</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-slate-600">{title}</span>
            </div>
            <h1 className="font-heading text-xl font-bold text-slate-900">{title}</h1>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input 
                type="text" 
                placeholder="Buscar..." 
                className="pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 w-64"
              />
            </div>
          </div>
        </div>

        {/* Mobile Page Title */}
        <div className="lg:hidden bg-white border-b border-slate-200 px-4 py-3">
          <h1 className="font-heading text-lg font-bold text-slate-900">{title}</h1>
        </div>

        {/* Page Content */}
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
